import * as React from 'react';
import type { ActiveDotProps, DotItemDotProps, LineProps } from 'recharts';
import {
  Area,
  CartesianGrid,
  ComposedChart as RechartsComposedChart,
  Dot,
  Line,
  Label,
  XAxis,
  YAxis,
} from 'recharts';
import { cn, compactNumber } from '../../lib/utils';
import { ChartContainer } from './ChartContainer';
import { ChartDataTable } from './ChartDataTable';
import { ChartTooltip } from './ChartTooltip';
import type {
  ChartConfig,
  ChartDataPoint,
  ChartPointValue,
  LineChartProps,
  LineDefinition,
} from './types';
import { isSyntheticPoint, resolveChartColor } from './utils';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import useI18n from '../../hooks/useI18n';
import { Typography } from '../Typography';

const DEFAULT_MARGIN = { top: 8, right: 20, bottom: 8, left: 12 };
const DEFAULT_FILL_COLOR = 'var(--color-blue-100)';
const DEFAULT_FILL_OPACITY = 0.3;
const DEFAULT_STROKE_WIDTH = 3;
const DEFAULT_DOT_RADIUS = 8;

export function LineChart<DataPoint extends ChartDataPoint = ChartDataPoint>({
  ariaDescription,
  ariaLabel,
  aspect,
  chartLabel,
  className,
  config,
  data,
  gridStrokeDasharray = '6 6',
  height,
  labelFormatter,
  legendProps,
  lines,
  margin,
  showAreaFill = true,
  showGrid = true,
  showLegend = false,
  showTooltip = true,
  valueFormatter,
  width = '100%',
  xAxisLabel,
  xAxisProps,
  xKey,
  yAxisProps,
}: LineChartProps<DataPoint>) {
  const { t, resolvedLanguage } = useI18n();
  const isLg = useTailwindBreakpoint('lg');
  const [hasLongLabels, setHasLongLabels] = React.useReducer(() => true, false);
  const captionId = React.useId();

  const hasData = data.length > 0;
  const xKeyString = xKey as string;

  // The figure is named by its visible caption where there is one, so that the
  // name announced is the text on screen; the graphic itself always carries the
  // explicit name.
  const caption = chartLabel ?? ariaLabel;

  const yAxisTickFormatter =
    yAxisProps?.tickFormatter ??
    ((value: ChartPointValue) => {
      const compact = compactNumber(value, resolvedLanguage);

      if (!hasLongLabels && String(compact).length >= 5) {
        setHasLongLabels();
      }

      return compact;
    });

  const derivedConfig = React.useMemo<ChartConfig>(() => {
    if (config) return config;

    return lines.reduce<ChartConfig>((acc, line, index) => {
      const key = line.dataKey as string;
      acc[key] = {
        label: line.label ?? '',
        color: resolveChartColor(line.color, index),
      };
      return acc;
    }, {} as ChartConfig);
  }, [config, lines]);

  const derivedXAxisTicks = React.useMemo<number[] | undefined>(() => {
    if (xAxisProps?.ticks) return undefined;

    const lineKeys = lines.map((line) => line.dataKey as string);
    if (lineKeys.length === 0) return undefined;

    const realTicks = new Set<number>();
    let hasSyntheticPoint = false;

    data.forEach((point) => {
      const xValue = point[xKeyString];
      const isNumericX = typeof xValue === 'number';
      if (!isNumericX) return;

      const syntheticPoint = isSyntheticPoint(point);
      if (syntheticPoint) {
        hasSyntheticPoint = true;
        return;
      }

      const hasAnyLineValue = lineKeys.some(
        (lineKey) => point[lineKey] != null,
      );
      if (!hasAnyLineValue) return;

      realTicks.add(xValue);
    });

    if (!hasSyntheticPoint || realTicks.size === 0) {
      return undefined;
    }

    return Array.from(realTicks).sort((a, b) => a - b);
  }, [data, lines, xAxisProps?.ticks, xKeyString]);

  const mergedXAxisProps = React.useMemo(
    () => ({
      ...(derivedXAxisTicks ? { ticks: derivedXAxisTicks } : {}),
      ...xAxisProps,
    }),
    [derivedXAxisTicks, xAxisProps],
  );

  if (!aspect) {
    aspect = isLg ? 1.6 : 2;
  }

  return (
    <figure className='eye:w-full' aria-labelledby={captionId}>
      <Typography
        tag='figcaption'
        id={captionId}
        variant='body-s'
        className={cn(
          'eye:justify-self-center eye:text-12! eye:text-grey-120! eye:md:text-12!',
          !chartLabel && 'eye:sr-only',
        )}
      >
        {caption}
      </Typography>
      {/* No `ariaLabel` here on purpose: the surface below carries the role
          and the name, and a second `role="img"` on the container would only
          announce the same graphic twice. */}
      <ChartContainer
        className={cn('eye:w-full', className)}
        config={derivedConfig}
        width={width}
        height={height}
        aspect={aspect}
      >
        {/* Recharts' accessibility layer makes the surface a keyboard operable
            `application`. Screen reader testing showed what that costs: NVDA
            switches to focus mode, reads every internal group as an unnamed
            "grouping", and never reaches the data table, while the active point
            is not exposed at all. The chart carries no interaction of its own,
            so it is a plain graphic instead, with the values in the table. */}
        <RechartsComposedChart
          accessibilityLayer={false}
          data={data}
          desc={ariaDescription}
          role='img'
          title={ariaLabel}
          margin={{
            ...DEFAULT_MARGIN,
            ...(hasLongLabels && { right: 36 }),
            ...margin,
          }}
        >
          {showGrid ? (
            <CartesianGrid
              vertical={!hasData}
              strokeDasharray={gridStrokeDasharray}
              stroke='var(--chart-grid-stroke, var(--color-grey-180))'
              strokeWidth={1}
            />
          ) : null}
          <XAxis
            dataKey={xKeyString}
            tickLine={false}
            axisLine={false}
            stroke='var(--chart-axis-stroke, var(--color-grey-120))'
            tickMargin={16}
            padding={{ left: DEFAULT_DOT_RADIUS, right: DEFAULT_DOT_RADIUS }}
            {...mergedXAxisProps}
          />
          {hasData ? (
            <YAxis
              orientation='right'
              tickLine={false}
              axisLine={false}
              stroke='var(--chart-axis-stroke, var(--color-grey-100))'
              tickMargin={16}
              tick={{
                fill: 'var(--chart-axis-tick-color, var(--color-grey-180))',
                fontWeight: 'var(--chart-axis-tick-font-weight, 600)',
              }}
              tickFormatter={yAxisTickFormatter}
              {...yAxisProps}
            />
          ) : (
            <Label position='center'>{t('noData')}</Label>
          )}
          {showTooltip ? (
            <ChartTooltip
              labelFormatter={labelFormatter}
              valueFormatter={
                valueFormatter
                  ? (value, item) => valueFormatter(value, item)
                  : undefined
              }
            />
          ) : null}
          {lines.map((line, index) => {
            const key = line.dataKey as string;
            const color = resolveLineColor(line, derivedConfig, index);
            const dotConfig = withSyntheticDotGuard(getDotConfig(line.dot));
            const activeDotConfig = withSyntheticActiveDotGuard({
              r: line.activeDotRadius || DEFAULT_DOT_RADIUS,
              strokeWidth: DEFAULT_STROKE_WIDTH,
            });

            return (
              <React.Fragment key={key}>
                {showAreaFill ? (
                  <Area
                    type={line.type || 'monotone'}
                    dataKey={key}
                    stroke='none'
                    fill={line.areaFill ?? DEFAULT_FILL_COLOR}
                    fillOpacity={DEFAULT_FILL_OPACITY}
                    baseValue={0}
                    dot={false}
                    activeDot={false}
                    tooltipType='none'
                    legendType='none'
                    isAnimationActive={false}
                  />
                ) : null}
                <Line
                  animationDuration={0}
                  type={line.type || 'monotone'}
                  dataKey={key}
                  stroke={color}
                  name={line.label || derivedConfig[key]?.label}
                  unit={line.unit}
                  strokeWidth={line.strokeWidth || DEFAULT_STROKE_WIDTH}
                  dot={dotConfig}
                  activeDot={activeDotConfig}
                />
              </React.Fragment>
            );
          })}
        </RechartsComposedChart>
      </ChartContainer>
      <ChartDataTable
        caption={ariaLabel}
        data={data}
        labelFormatter={labelFormatter}
        lines={lines}
        valueFormatter={valueFormatter}
        xAxisLabel={xAxisLabel ?? xKeyString}
        xKey={xKey}
      />
      {showLegend && hasData ? (
        <LineChartLegend
          lines={lines}
          derivedConfig={derivedConfig}
          legendProps={legendProps}
        />
      ) : null}
    </figure>
  );
}

function resolveLineColor<DataPoint extends ChartDataPoint>(
  line: LineDefinition<DataPoint>,
  derivedConfig: ChartConfig,
  index: number,
) {
  const key = line.dataKey as string;
  return (
    line.color ||
    derivedConfig[key]?.color ||
    resolveChartColor(undefined, index)
  );
}

function LineChartLegend<DataPoint extends ChartDataPoint>({
  lines,
  derivedConfig,
  legendProps,
}: {
  lines: LineDefinition<DataPoint>[];
  derivedConfig: ChartConfig;
  legendProps?: LineChartProps<DataPoint>['legendProps'];
}) {
  return (
    <div
      {...legendProps}
      className={cn(
        'eye:mt-12 eye:flex eye:flex-wrap eye:items-center eye:justify-center eye:gap-x-24 eye:gap-y-8',
        legendProps?.className,
      )}
      role='list'
      aria-label='Chart legend'
    >
      {lines.map((line, index) => {
        const key = line.dataKey as string;
        const color = resolveLineColor(line, derivedConfig, index);
        const label = line.label ?? derivedConfig[key]?.label ?? key;
        return (
          <div
            key={key}
            role='listitem'
            className='eye:flex eye:items-center eye:gap-8'
          >
            <svg
              aria-hidden='true'
              width={28}
              height={12}
              viewBox='0 0 28 12'
              focusable='false'
              className='eye:shrink-0'
            >
              <line
                x1={0}
                y1={6}
                x2={28}
                y2={6}
                stroke={color}
                strokeWidth={3}
                strokeLinecap='round'
              />
              <circle cx={14} cy={6} r={3.5} fill={color} />
            </svg>
            <span className='eye:body-m eye:font-medium eye:text-grey-160'>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function getDotConfig(value: LineProps['dot']) {
  switch (typeof value) {
    case 'boolean':
      return value
        ? { r: DEFAULT_DOT_RADIUS, strokeWidth: DEFAULT_STROKE_WIDTH }
        : false;
    case 'object':
      return value;
    case 'function':
      return value;
    default:
      return undefined;
  }
}

function withSyntheticDotGuard(dot: LineProps['dot']): LineProps['dot'] {
  if (dot == null || dot === false) {
    return dot;
  }

  return (props: DotItemDotProps) => {
    if (isSyntheticPoint(props.payload)) {
      return null;
    }

    if (typeof dot === 'function') {
      return dot(props);
    }

    if (React.isValidElement(dot)) {
      return React.cloneElement(
        dot,
        props as unknown as React.SVGProps<SVGElement>,
      );
    }

    if (dot === true) {
      return (
        <Dot {...(props as unknown as React.ComponentProps<typeof Dot>)} />
      );
    }

    return (
      <Dot
        {...(props as unknown as React.ComponentProps<typeof Dot>)}
        {...dot}
      />
    );
  };
}

function withSyntheticActiveDotGuard(
  activeDot: LineProps['activeDot'],
): LineProps['activeDot'] {
  if (activeDot == null || activeDot === false) {
    return activeDot;
  }

  return (props: ActiveDotProps) => {
    if (isSyntheticPoint(props.payload)) {
      return null;
    }

    if (typeof activeDot === 'function') {
      return activeDot(props);
    }

    if (React.isValidElement(activeDot)) {
      return React.cloneElement(
        activeDot,
        props as unknown as React.SVGProps<SVGElement>,
      );
    }

    if (activeDot === true) {
      return (
        <Dot {...(props as unknown as React.ComponentProps<typeof Dot>)} />
      );
    }

    return (
      <Dot
        {...(props as unknown as React.ComponentProps<typeof Dot>)}
        {...activeDot}
      />
    );
  };
}
